import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Card, Collapse } from 'antd';
import TransportFormSectionTitle from './TransportFormSectionTitle';
import './TransportFormSection.css';

/**
 * Collapsible section shell for the transport form (operator feedback
 * 2026-09: "wszystkie sekcje w akordeonach, zwinięte by default").
 *
 * Every section is closed on mount. Panels are force-rendered so the antd
 * Form.Items inside register immediately — validation, useWatch and the
 * payload builder must see every field whether or not its panel is open.
 * Open state lives in TransportFormSectionsProvider so the required-field
 * links and the submit-failure handler can reveal the right section.
 */
export type TransportFormSectionKey = 'carrier' | 'route' | 'orders' | 'costs' | 'vehicle';

interface TransportFormSectionsContextValue {
  openKeys: TransportFormSectionKey[];
  setOpen: (key: TransportFormSectionKey, open: boolean) => void;
  openSection: (key: TransportFormSectionKey) => void;
  openAll: () => void;
}

const ALL_KEYS: TransportFormSectionKey[] = ['carrier', 'route', 'orders', 'costs', 'vehicle'];

const TransportFormSectionsContext = createContext<TransportFormSectionsContextValue | null>(null);

export const TransportFormSectionsProvider: React.FC<{
  children: React.ReactNode;
  initialOpen?: TransportFormSectionKey[];
}> = ({ children, initialOpen = [] }) => {
  const [openKeys, setOpenKeys] = useState<TransportFormSectionKey[]>(initialOpen);

  const setOpen = useCallback((key: TransportFormSectionKey, open: boolean) => {
    setOpenKeys((current) => {
      const has = current.includes(key);
      if (open === has) return current;
      return open ? [...current, key] : current.filter((item) => item !== key);
    });
  }, []);
  const openSection = useCallback((key: TransportFormSectionKey) => setOpen(key, true), [setOpen]);
  const openAll = useCallback(() => setOpenKeys(ALL_KEYS), []);

  const value = useMemo(
    () => ({ openKeys, setOpen, openSection, openAll }),
    [openKeys, setOpen, openSection, openAll],
  );
  return (
    <TransportFormSectionsContext.Provider value={value}>
      {children}
    </TransportFormSectionsContext.Provider>
  );
};

// Outside the provider (isolated section unit tests) the section behaves as
// an always-open card so existing tests keep exercising the fields.
export function useTransportFormSections(): TransportFormSectionsContextValue | null {
  return useContext(TransportFormSectionsContext);
}

interface TransportFormSectionProps {
  sectionKey: TransportFormSectionKey;
  step: number;
  icon: React.ReactNode;
  label: string;
  /** Short read-only summary shown in the header while the panel is closed. */
  summary?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

const TransportFormSection: React.FC<TransportFormSectionProps> = ({
  sectionKey,
  step,
  icon,
  label,
  summary,
  className,
  children,
}) => {
  const sections = useTransportFormSections();
  const isOpen = sections ? sections.openKeys.includes(sectionKey) : true;

  return (
    <Card
      className={['transport-form-section', className].filter(Boolean).join(' ')}
      data-testid={`transport-form-section-${sectionKey}`}
      data-open={isOpen ? 'true' : 'false'}
      styles={{ body: { padding: 0 } }}
    >
      <Collapse
        ghost
        activeKey={isOpen ? [sectionKey] : []}
        onChange={(keys) => {
          const next = (Array.isArray(keys) ? keys : [keys]).includes(sectionKey);
          sections?.setOpen(sectionKey, next);
        }}
        items={[{
          key: sectionKey,
          forceRender: true,
          label: (
            <div className="transport-form-section__header">
              <TransportFormSectionTitle step={step} icon={icon} label={label} />
              {!isOpen && summary ? (
                <span className="transport-form-section__summary">{summary}</span>
              ) : null}
            </div>
          ),
          children: <div className="transport-form-section__body">{children}</div>,
        }]}
      />
    </Card>
  );
};

export default TransportFormSection;
